/**
 * Date utility functions for consistent date formatting across the application
 */

/**
 * Formats a date to "Fri 10 Oct 2025" format
 * @param {Date|string|Object} dateInput - Date object, date string, or Firebase timestamp
 * @returns {string} Formatted date string in "Fri 10 Oct 2025" format
 */
export const formatDateToCustomFormat = (dateInput) => {
  let date;
  
  // Handle different input types
  if (typeof dateInput === 'string') {
    date = new Date(dateInput);
  } else if (dateInput && dateInput.seconds) {
    // Firebase timestamp format
    date = new Date(dateInput.seconds * 1000);
  } else if (dateInput instanceof Date) {
    date = dateInput;
  } else {
    date = new Date();
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  
  // Format options for the custom format
  const options = {
    weekday: 'short', // Fri
    day: 'numeric',   // 10
    month: 'short',   // Oct
    year: 'numeric'   // 2025
  };
  
  return date.toLocaleDateString('en-US', options);
};

/**
 * Helper function to handle both string and Firebase timestamp formats
 * This maintains compatibility with existing getDateFromCreatedAt functions
 * @param {Date|string|Object} createdAt - Date input in various formats
 * @returns {Date} Date object
 */
export const getDateFromCreatedAt = (createdAt) => {
  if (!createdAt) {
    return new Date(0);
  }
  if (createdAt instanceof Date) {
    return isNaN(createdAt.getTime()) ? new Date(0) : createdAt;
  }
  if (typeof createdAt === "string") {
    const d = new Date(createdAt);
    return isNaN(d.getTime()) ? new Date(0) : d;
  }
  if (typeof createdAt?.toDate === "function") {
    try {
      const d = createdAt.toDate();
      return isNaN(d.getTime()) ? new Date(0) : d;
    } catch (e) {
      return new Date(0);
    }
  }
  if (typeof createdAt?.seconds === "number") {
    return new Date(createdAt.seconds * 1000);
  }
  if (typeof createdAt?._seconds === "number") {
    return new Date(createdAt._seconds * 1000);
  }
  const d = new Date(createdAt);
  return isNaN(d.getTime()) ? new Date(0) : d;
};

/**
 * Formats a date to "Fri 10 Oct 2025" format with time handling
 * @param {Date|string|Object} dateInput - Date input in various formats
 * @returns {string} Formatted date string
 */
export const formatDateTimeToCustomFormat = (dateInput) => {
  const date = getDateFromCreatedAt(dateInput);
  return formatDateToCustomFormat(date);
};

/**
 * Formats a date for admin panels with 24-hour time format
 * Returns format like "Fri 10 Oct 2025 14:30:25"
 * @param {Date|string|Object} dateInput - Date input in various formats
 * @returns {string} Formatted date-time string with 24-hour time
 */
export const formatDateTimeForAdmin = (dateInput) => {
  let date;
  
  // Handle different input types
  if (typeof dateInput === 'string') {
    date = new Date(dateInput);
  } else if (dateInput && dateInput.seconds) {
    // Firebase timestamp format
    date = new Date(dateInput.seconds * 1000);
  } else if (dateInput instanceof Date) {
    date = dateInput;
  } else {
    date = new Date();
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  
  // Format date part
  const dateOptions = {
    weekday: 'short', // Fri
    day: 'numeric',   // 10
    month: 'short',   // Oct
    year: 'numeric'   // 2025
  };
  
  const datePart = date.toLocaleDateString('en-US', dateOptions);
  
  // Format time part in 24-hour format
  const timeOptions = {
    hour12: false,    // 24-hour format
    hour: '2-digit',  // HH
    minute: '2-digit', // MM
    second: '2-digit'  // SS
  };
  
  const timePart = date.toLocaleTimeString('en-US', timeOptions);
  
  return `${datePart} ${timePart}`;
};